import React, { useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  useGetTechnologiesQuery,
  useAddTechnologyMutation,
  useUpdateTechnologyMutation,
  useDeleteTechnologyMutation,
  useGetCategoriesQuery,
  useGetParcoursQuery,
} from "../../redux/api/coachApi";

const TechnologiesView = () => {
  const [newTechnology, setNewTechnology] = useState({
    name: "",
    description: "",
    category: 0,
    parcours: 0,
  });
  const [editingTechnology, setEditingTechnology] = useState(null);
  const [error, setError] = useState("");

  // RTK Query hooks
  const {
    data: technologies,
    isLoading,
    error: fetchError,
  } = useGetTechnologiesQuery();
  const { data: categories = [], isLoading: isCategoriesLoading } =
    useGetCategoriesQuery();
  const { data: parcoursData = [], isLoading: isParcoursLoading } =
    useGetParcoursQuery();
  const [addTechnology, { isLoading: isAdding }] = useAddTechnologyMutation();
  const [updateTechnology, { isLoading: isUpdating }] =
    useUpdateTechnologyMutation();
  const [deleteTechnology, { isLoading: isDeleting }] =
    useDeleteTechnologyMutation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newTechnology.name.trim()) {
      setError("Le nom de la technologie est requis.");
      return;
    }
    setError("");

    try {
      if (editingTechnology) {
        await updateTechnology({
          id: editingTechnology.id,
          ...newTechnology,
        }).unwrap();
      } else {
        await addTechnology(newTechnology).unwrap();
      }
      setNewTechnology({ name: "", description: "", category: 0, parcours: 0 });
      setEditingTechnology(null);
    } catch (err) {
      setError(
        editingTechnology
          ? "Erreur lors de la mise à jour de la technologie."
          : "Erreur lors de l'ajout de la technologie."
      );
      console.error("Error submitting technology:", err);
    }
  };

  const handleEdit = (technology) => {
    setEditingTechnology(technology);
    setNewTechnology({
      name: technology.name,
      description: technology.description,
      category: technology.category?.id ?? technology.category,
      parcours: technology.parcours?.id ?? technology.parcours,
    });
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cette technologie ?")
    ) {
      try {
        await deleteTechnology(id).unwrap();
      } catch (err) {
        setError("Erreur lors de la suppression de la technologie.");
        console.error("Error deleting technology:", err);
      }
    }
  };

  if (isLoading) {
    return <div className="text-center text-gray-500">Chargement...</div>;
  }

  if (fetchError) {
    return (
      <div className="text-red-500 text-center">
        Erreur : {fetchError.message}
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto p-6 space-y-6">
      {/* Form Card */}
      <div className="bg-white  shadow-md rounded-2xl p-6">
        <h2 className="text-xl font-bold text-teal-700 mb-4 text-center">
          {editingTechnology
            ? "Modifier une Technologie"
            : "Ajouter une Technologie"}
        </h2>
        {error && (
          <div className="text-red-500 text-sm mb-3 text-center">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={newTechnology.name}
            onChange={(e) =>
              setNewTechnology({ ...newTechnology, name: e.target.value })
            }
            placeholder="Entrez le nom de la technologie"
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          {/* Description */}
          <textarea
            value={newTechnology.description}
            onChange={(e) =>
              setNewTechnology({
                ...newTechnology,
                description: e.target.value,
              })
            }
            placeholder="Entrez une description"
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
          {/* Catégorie */}
          <select
            value={newTechnology.category}
            onChange={(e) =>
              setNewTechnology({
                ...newTechnology,
                category: parseInt(e.target.value),
              })
            }
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={0}>Sélectionner une catégorie</option>
            {categories &&
              categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom}
                </option>
              ))}
          </select>
          {/* Parcours */}
          <select
            value={newTechnology.parcours}
            onChange={(e) =>
              setNewTechnology({
                ...newTechnology,
                parcours: parseInt(e.target.value),
              })
            }
            className="w-full border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={0}>Sélectionner un parcours</option>
            {parcoursData &&
              parcoursData.map((p) => (
                <option key={p.id} value={p.id} className="text-black">
                  {p.name}
                </option>
              ))}
          </select>
          <button
            type="submit"
            className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-xl transition"
            disabled={isAdding || isUpdating}
          >
            {editingTechnology ? "Mettre à jour" : "Ajouter"}
          </button>
        </form>
      </div>

      {/* List Card */}
      <div className="bg-white shadow-md rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-teal-700 mb-4">
          Liste des Technologies
        </h2>
        {technologies && technologies.length > 0 ? (
          <div className="space-y-3">
            {technologies.map((tech) => (
              <div
                key={tech.id}
                className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded-xl transition"
              >
                <span className="font-medium text-gray-700">{tech.name}</span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleEdit(tech)}
                    disabled={isUpdating}
                    className="text-blue-500 hover:text-blue-700 transition"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => handleDelete(tech.id)}
                    disabled={isDeleting}
                    className="text-red-500 hover:text-red-700 transition"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center">
            Aucune technologie trouvée.
          </p>
        )}
      </div>
    </div>
  );
};

export default TechnologiesView;
